import React, { useState, useEffect, useReducer, useContext, useRef } from "react";
import { toast } from "react-toastify";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import IconButton from "@material-ui/core/IconButton";
import Fab from "@material-ui/core/Fab";
import Typography from "@material-ui/core/Typography";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import BlockIcon from "@material-ui/icons/Block";
import AddIcon from "@material-ui/icons/Add";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import PeopleIcon from "@material-ui/icons/People";
import PublishIcon from "@material-ui/icons/Publish";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactListItemModal from "../../components/ContactListItemModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import useContactLists from "../../hooks/useContactLists";
import ForbiddenPage from "../../components/ForbiddenPage";
import planilhaExemplo from "../../assets/planilha.xlsx";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];
    contacts.forEach((c) => {
      const idx = state.findIndex((s) => s.id === c.id);
      if (idx !== -1) state[idx] = c; else newContacts.push(c);
    });
    return [...state, ...newContacts];
  }
  if (action.type === "UPDATE_CONTACTS") {
    const c = action.payload;
    const idx = state.findIndex((s) => s.id === c.id);
    if (idx !== -1) { state[idx] = c; return [...state]; }
    return [c, ...state];
  }
  if (action.type === "DELETE_CONTACT") {
    const idx = state.findIndex((s) => s.id === action.payload);
    if (idx !== -1) state.splice(idx, 1);
    return [...state];
  }
  if (action.type === "RESET") return [];
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1, padding: theme.spacing(1),
    overflowY: "scroll", ...theme.scrollbarStyles,
  },
  mobileList: {
    display: "flex", flexDirection: "column",
    gap: theme.spacing(1.5), padding: theme.spacing(1),
  },
  card: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10, padding: theme.spacing(1.5),
    display: "flex", alignItems: "center", gap: theme.spacing(1.5),
  },
  cardStatus: {
    width: 36, height: 36, borderRadius: "50%",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  cardBody: { flex: 1, minWidth: 0 },
  cardName: { fontWeight: 700, fontSize: "0.95rem", color: theme.palette.text.primary },
  cardMeta: { fontSize: "0.78rem", color: theme.palette.text.secondary, marginTop: 2 },
  cardActions: { display: "flex", flexShrink: 0, gap: 2 },
  mobileHeader: {
    display: "flex", flexDirection: "column",
    gap: theme.spacing(1), width: "100%", padding: theme.spacing(0, 0, 1),
  },
  mobileHeaderRow: {
    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
  },
  fab: { position: "fixed", bottom: theme.spacing(3), right: theme.spacing(3), zIndex: 10 },
  emptyState: {
    display: "flex", flexDirection: "column", alignItems: "center",
    justifyContent: "center", padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary, gap: theme.spacing(1),
  },
}));

const ContactListItems = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user, socket } = useContext(AuthContext);
  const { contactListId } = useParams();
  const history = useHistory();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactListItemModalOpen, setContactListItemModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [contactList, setContactList] = useState({});
  const fileUploadRef = useRef(null);
  const { findById: findContactList } = useContactLists();

  useEffect(() => {
    findContactList(contactListId).then((data) => setContactList(data));
  }, [contactListId]);

  useEffect(() => { dispatch({ type: "RESET" }); setPageNumber(1); }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`contact-list-items`, { params: { searchParam, pageNumber, contactListId } });
        dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
        setHasMore(data.hasMore);
        setLoading(false);
      } catch (err) { toastError(err); }
    }, 500);
    return () => clearTimeout(t);
  }, [searchParam, pageNumber, contactListId]);

  useEffect(() => {
    const companyId = user.companyId;
    const onEvent = (data) => {
      if (data.action === "update" || data.action === "create") dispatch({ type: "UPDATE_CONTACTS", payload: data.record });
      if (data.action === "delete") dispatch({ type: "DELETE_CONTACT", payload: +data.id });
      if (data.action === "reload") dispatch({ type: "LOAD_CONTACTS", payload: data.records });
    };
    socket.on(`company-${companyId}-ContactListItem`, onEvent);
    return () => socket.off(`company-${companyId}-ContactListItem`, onEvent);
  }, [contactListId]);

  const handleOpenModal = () => { setSelectedContactId(null); setContactListItemModalOpen(true); };
  const handleCloseModal = () => { setSelectedContactId(null); setContactListItemModalOpen(false); };
  const handleEdit = (id) => { setSelectedContactId(id); setContactListItemModalOpen(true); };
  const handleDelete = async (id) => {
    try {
      await api.delete(`/contact-list-items/${id}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) { toastError(err); }
    setDeletingContact(null); setSearchParam(""); setPageNumber(1);
  };
  const handleImport = async () => {
    try {
      const formData = new FormData();
      formData.append("file", fileUploadRef.current.files[0]);
      await api.request({ url: `contact-lists/${contactListId}/upload`, method: "POST", data: formData });
    } catch (err) { toastError(err); }
  };
  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) setPageNumber((p) => p + 1);
  };

  const renderMobileCard = (contact) => (
    <div key={contact.id} className={classes.card}>
      <div className={classes.cardStatus}>
        {contact.isWhatsappValid
          ? <CheckCircleIcon htmlColor="green" fontSize="small" />
          : <BlockIcon htmlColor="grey" fontSize="small" />}
      </div>
      <div className={classes.cardBody}>
        <div className={classes.cardName}>{contact.name}</div>
        <div className={classes.cardMeta}>{contact.number}</div>
        {contact.email && <div className={classes.cardMeta}>{contact.email}</div>}
      </div>
      <div className={classes.cardActions}>
        <IconButton size="small" onClick={() => handleEdit(contact.id)}><EditIcon fontSize="small" /></IconButton>
        <Can role={user.profile} perform="contacts-page:deleteContact" yes={() => (
          <IconButton size="small" onClick={() => { setConfirmOpen(true); setDeletingContact(contact); }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )} />
      </div>
    </div>
  );

  return (
    <MainContainer>
      <ContactListItemModal
        open={contactListItemModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      />
      <ConfirmationModal
        title={deletingContact
          ? `${i18n.t("contactListItems.confirmationModal.deleteTitle")} ${deletingContact.name}?`
          : i18n.t("contactListItems.confirmationModal.importTitlte")}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => deletingContact ? handleDelete(deletingContact.id) : handleImport()}
      >
        {deletingContact
          ? i18n.t("contactListItems.confirmationModal.deleteMessage")
          : <>{i18n.t("contactListItems.confirmationModal.importMessage")}<a href={planilhaExemplo} download="planilha.xlsx"> Descargar planilla ejemplo.</a></>}
      </ConfirmationModal>

      <input style={{ display: "none" }} id="upload" name="file" type="file" accept=".xls,.xlsx"
        onChange={() => setConfirmOpen(true)} ref={fileUploadRef} />

      {user.profile === "user" && user?.showCampaign === "disabled" ? <ForbiddenPage /> : (
        <>
          {isMobile ? (
            <>
              <MainHeader>
                <div className={classes.mobileHeader}>
                  <div className={classes.mobileHeaderRow}>
                    <IconButton size="small" onClick={() => history.push("/contact-lists")}>
                      <ArrowBackIcon />
                    </IconButton>
                    <Title style={{ flex: 1 }}>{contactList.name}</Title>
                    <IconButton size="small" onClick={() => { fileUploadRef.current.value = null; fileUploadRef.current.click(); }}>
                      <PublishIcon />
                    </IconButton>
                  </div>
                  <TextField
                    fullWidth size="small" variant="outlined"
                    placeholder={i18n.t("contactListItems.searchPlaceholder")}
                    type="search" value={searchParam} onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: "gray" }} fontSize="small" /></InputAdornment> }}
                  />
                </div>
              </MainHeader>
              <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
                {contacts.length === 0 && !loading ? (
                  <div className={classes.emptyState}>
                    <PeopleIcon style={{ fontSize: 48, opacity: 0.3 }} />
                    <Typography variant="body2">Sin contactos en esta lista</Typography>
                  </div>
                ) : (
                  <div className={classes.mobileList}>
                    {contacts.map(renderMobileCard)}
                    {loading && <Typography variant="caption" align="center" color="textSecondary">Cargando...</Typography>}
                  </div>
                )}
              </Paper>
              <Fab color="primary" className={classes.fab} onClick={handleOpenModal}><AddIcon /></Fab>
            </>
          ) : (
            <>
              <MainHeader>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <IconButton size="small" onClick={() => history.push("/contact-lists")}><ArrowBackIcon /></IconButton>
                    <Title>{contactList.name}</Title>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <TextField
                      placeholder={i18n.t("contactListItems.searchPlaceholder")}
                      type="search" value={searchParam}
                      onChange={(e) => setSearchParam(e.target.value.toLowerCase())}
                      size="small" variant="outlined"
                      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon style={{ color: "gray" }} /></InputAdornment> }}
                    />
                    <Button variant="contained" color="primary" onClick={() => { fileUploadRef.current.value = null; fileUploadRef.current.click(); }}>
                      {i18n.t("contactListItems.buttons.import")}
                    </Button>
                    <Button variant="contained" color="primary" onClick={handleOpenModal}>
                      {i18n.t("contactListItems.buttons.add")}
                    </Button>
                  </div>
                </div>
              </MainHeader>
              <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" style={{ width: "0%" }}>#</TableCell>
                      <TableCell>{i18n.t("contactListItems.table.name")}</TableCell>
                      <TableCell align="center">{i18n.t("contactListItems.table.number")}</TableCell>
                      <TableCell align="center">{i18n.t("contactListItems.table.email")}</TableCell>
                      <TableCell align="center">{i18n.t("contactListItems.table.actions")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contacts.map((contact) => (
                      <TableRow key={contact.id}>
                        <TableCell align="center" style={{ width: "0%" }}>
                          <IconButton>
                            {contact.isWhatsappValid
                              ? <CheckCircleIcon titleAccess="Whatsapp Válido" htmlColor="green" />
                              : <BlockIcon titleAccess="Whatsapp Inválido" htmlColor="grey" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{contact.name}</TableCell>
                        <TableCell align="center">{contact.number}</TableCell>
                        <TableCell align="center">{contact.email}</TableCell>
                        <TableCell align="center">
                          <IconButton size="small" onClick={() => handleEdit(contact.id)}><EditIcon /></IconButton>
                          <Can role={user.profile} perform="contacts-page:deleteContact" yes={() => (
                            <IconButton size="small" onClick={() => { setConfirmOpen(true); setDeletingContact(contact); }}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          )} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {loading && <TableRowSkeleton columns={4} />}
                  </TableBody>
                </Table>
              </Paper>
            </>
          )}
        </>
      )}
    </MainContainer>
  );
};

export default ContactListItems;
